import type { MorphemeDetailsTileSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { hyphenateSync } from "@-/com.npmjs.hyphen/en-us";
import { stringLength } from "@-/com.npmjs.string-length";
import { cn } from "@-/com.npmjs.cn";
import { View } from "@-/com.npmjs.react-native";
import { useMemo } from "react";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { usePrevalentPinyin } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.pinyin.prevalence";
import { toDiacriticPinyin } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";
import { pinyinForGrapheme } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.glyph";
import type { CardTileType } from "#card-tile-type";
import { getGlossSizing } from "#sizing";

/**
 * The "answer" shown in the bottom half of a card tile, centered and allowed to
 * wrap to two lines: the gloss for a meaning card, the tone-marked reading for a
 * pronunciation card. Both render identically so the two card types stay visually
 * aligned.
 */
export function CardDetailsTileBackContent({
  cardType,
  graphemeCard,
  isDetailsPending = false,
  pinyin = null,
  size,
}: {
  cardType: CardTileType;
  graphemeCard: MorphemeDetailsTileSlice;
  /**
   * The slice is a provisional one built from the local actor DB and the full content/static
   * details are still loading. When that provisional slice has no answer text yet (a word card's
   * definition lives only in the static DB), show an ellipsis instead of the settled "-" so the
   * tile reads as "loading", not "no gloss".
   */
  isDetailsPending?: boolean;
  /** A pronunciation card's own tone-number reading, shown instead of the character's main one. */
  pinyin?: string | null;
  size: number;
}) {
  const studyPinyinList = usePrevalentPinyin(graphemeCard.front, graphemeCard.pinyinList);
  const mainPinyin = usePrevalentPinyin(
    graphemeCard.front,
    pinyinForGrapheme(graphemeCard.front) ?? "",
  );
  const text = useMemo(() => {
    const missingText = isDetailsPending ? "…" : "-";
    if (cardType === "pronunciation") {
      if (pinyin !== null && pinyin.length > 0) {
        return toDiacriticPinyin(pinyin);
      }
      if (mainPinyin && stringLength(graphemeCard.front) === 1) {
        return toDiacriticPinyin(mainPinyin);
      }
      const primary = studyPinyinList[0]?.toneNumbersPinyin;
      return primary === undefined ? missingText : toDiacriticPinyin(primary);
    }
    // We intentionally use || here to handle empty strings.
    return graphemeCard.back || studyPinyinList[0]?.gloss || missingText;
  }, [
    cardType,
    mainPinyin,
    graphemeCard.back,
    graphemeCard.front,
    isDetailsPending,
    pinyin,
    studyPinyinList,
  ]);
  const { fontSize, lineHeight } = getGlossSizing(size);
  const isMnemonic =
    cardType === "meaning" &&
    graphemeCard.morphemeType === "grapheme" &&
    Boolean(graphemeCard.back) &&
    graphemeCard.label === "mnemonic";

  return (
    <View className="min-h-0 min-w-0 flex-1 items-center justify-center self-stretch px-1">
      <Text
        accessibilityLabel={text}
        className={cn("self-stretch text-center font-sans text-foreground", isMnemonic && "italic")}
        ellipsizeMode="tail"
        numberOfLines={2}
        style={{ fontSize, lineHeight }}
      >
        {cardType === "meaning" ? hyphenateSync(text) : text}
      </Text>
    </View>
  );
}
